import { Card, CardContent } from "@/components/ui/card";
import { IconStarFilled } from "@tabler/icons-react";
import { getReviewsByLanguage } from "@/content/reviews";
import SocialProof from "@/components/common/SocialProof";
import { useTranslation } from "react-i18next";
import ExpandableText from "@/components/common/ExpandableText";
import { useEffect, useMemo, useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Plans from "@/components/tabs/overview/Plans";
import AboutSection from "@/components/tabs/overview/AboutSection";

const Reviews = ({ onPurchase }: { onPurchase: (planId: string) => void }) => {
  const { t, i18n } = useTranslation();
  const [minHeight, setMinHeight] = useState(0);
  const tempRef = useRef<HTMLParagraphElement>(null);

  const [ratingFilter, setRatingFilter] = useState(0); // 0 for all
  const [sortBy, setSortBy] = useState("highest"); // 'highest', 'lowest', or 'newest'

  // Get reviews based on current language
  const reviews = useMemo(() => getReviewsByLanguage(i18n.language), [i18n.language]);

  useEffect(() => {
    if (tempRef.current) {
      const lineHeight = parseFloat(getComputedStyle(tempRef.current).lineHeight);
      setMinHeight(lineHeight * 3);
    }
  }, []);

  const filteredAndSortedReviews = useMemo(() => {
    let filtered = reviews;

    if (ratingFilter > 0) {
      filtered = reviews.filter(review => review.rating === ratingFilter);
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      } else if (sortBy === 'highest') {
        // If sorting by rating, prioritize rating, then date (newest first)
        if (b.rating !== a.rating) {
          return b.rating - a.rating;
        } else {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
      } else if (sortBy === 'lowest') {
        // If sorting by rating, prioritize rating, then date (newest first)
        if (a.rating !== b.rating) {
          return a.rating - b.rating;
        } else {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
      }
      // Default to sorting by date if no specific rating sort is applied, or if ratings are equal
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    return sorted;
  }, [reviews, ratingFilter, sortBy]);


  return (
    <div className="space-y-6">
      <div className="space-y-6 px-4 xl:px-0">
        <p ref={tempRef} className="text-slate-600 text-sm md:text-base whitespace-pre-line" style={{ visibility: 'hidden', position: 'absolute' }}>
          A<br/>A<br/>A
        </p>
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-3xl font-semibold">{t('reviews.title')}</h2>
          <SocialProof darkBackground={false} className="mt-4" />
          <p className="text-slate-600 mt-4 text-sm">{t('reviews.subtitle')}</p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 justify-start items-start md:items-center mb-4 mx-4 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{t('reviews.filterByRating')}</span>
            <Select value={ratingFilter.toString()} onValueChange={(value) => setRatingFilter(parseInt(value))}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t('reviews.allStars')}</SelectItem>
                {[5, 4, 3, 2, 1].map(rating => (
                  <SelectItem key={rating} value={rating.toString()}>
                    <div className="flex items-center gap-2 cursor-pointer">
                      {rating}
                      <div className="flex gap-1">
                      {[...Array(rating)].map((_, i) => (
                        <IconStarFilled key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{t('reviews.sortBy')}</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('reviews.sortBy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t('reviews.newest')}</SelectItem>
                <SelectItem value="highest">{t('reviews.highestRating')}</SelectItem>
                <SelectItem value="lowest">{t('reviews.lowestRating')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {filteredAndSortedReviews.map((review, index) => (
            <Card key={index} className="rounded-xl flex flex-col">
              <CardContent className="pt-6 pb-8 md:pt-8 md:pb-10 px-6 md:px-8 flex-1 flex flex-col">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row items-center gap-3 mb-2">
                    <div className="flex gap-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <IconStarFilled key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                      {[...Array(5 - review.rating)].map((_, i) => (
                        <IconStarFilled key={i} className="w-4 h-4 fill-slate-300 text-slate-300" />
                      ))}
                    </div>
                    <div className="w-px h-4 bg-slate-200" />
                    <p className="text-slate-600 text-sm whitespace-pre-line">{review.name}</p>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <p className="text-slate-600 text-base font-semibold whitespace-pre-line">{review.title}</p>
                    <p className="text-slate-300 text-xs whitespace-pre-line">{review.date}</p>
                  </div>
                  <ExpandableText text={review.content} className="text-slate-600 text-sm md:text-base whitespace-pre-line" minHeight={minHeight} />
                </div>

                {review.reply && (
                  <div className="mt-auto">
                    <hr className="h-px bg-slate-200 my-4" />
                    <ExpandableText text={review.reply} className="text-slate-600 text-sm md:text-base whitespace-pre-line" minHeight={minHeight} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

      </div>

      <Plans onPurchase={onPurchase} />
      <AboutSection />
    </div>
  );
};

export default Reviews;
